// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import apiService from '../../../../share/util/api/service';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import util from '../../../../share/util/utils';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';
import { convertUniqueCount } from '../../individualWorkSupport/REQ-05/req05_common_logic';
import resource from '../../../../share/util/resourceMap';

export default {
  name: "workSupportPageReq05",
  data() {
    return {
      processing: false,
      recollectedData: [],
      readResponse: [],
      tableItems: [],
      cstmListItem: [],
      filteredData: [],
      measuresId: "",
      immutableId: null,
      emptyListFfdStatus: '',
    };
  },

  watch: {
    language() {
      this.updateColumns();
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
  },

  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('cache', ['cacheFFDStores']),

    /**
     * REQ-05の作業サポート一覧取得
     * @param {String} projectId プロジェクトID
     * @param {String} measuresId 計測データID
     * @param {String} system ECU名
     * @param {Number} immutableId 不変ID
     */
    async createWorkSupportReq05MeasuresList(projectId, measuresId, immutableId) {
      try {
        this.processing = true;
        this.measuresId = measuresId;
        const workSupportEndPoint = '/workSupports/' + projectId + '/' + measuresId;
        const params = { region_info: this.cachedProjectDataSource };
        const resWorkSupport = await apiService.getParamCall(apiService.WSP, workSupportEndPoint, params, null);

        handler.validate(handler.validateTypes.all, resWorkSupport, this, null, async () => {
          this.immutableId = immutableId;
          this.readResponse = resWorkSupport.data.project.dtc_list;
          this.filteredData = this.readResponse.filter(d => d.code !== null);
          // DTC無しリスト且つ、FFD状態が存在する場合、FFD状態を保持する
          if (this.filteredData.length <= 0){
            if (this.readResponse.find(item => item.ffd_status === 'ng')) {
              this.emptyListFfdStatus = 'ng';
            } else if (this.readResponse.find(item => item.ffd_status === 'no')) {
              this.emptyListFfdStatus = 'no';
            }
          } 
          this.updateColumns();
          this.setCstmListItem();
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    /** 初期表示で必要な情報を設定 */
    // DTC表示画面 カラム更新
    // description項目 ⇒ 画面上は「名称」表示だが、既存のoriginalSortFuncを使用するため記載を合わせる
    updateColumns() {
      this.tableItems = [
        { name: 'dtc', label: 'message.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'description', label: 'individual.header_req05_name', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
        { name: 'tripCount', label: 'message.header_vehicle_dtc_table_igon_counts', originalSortFunc: diagnoseResource.sortIgon },
        { name: 'timeCount', label: 'message.header_vehicle_dtc_table_igon_times', originalSortFunc: diagnoseResource.sortIgon },
        { name: 'uniqueCount', label: 'individual.header_req05_dtc_table_count' },
        { name: 'ffd', label: 'message.header_vehicle_dtc_table_ffd', useButton: true, originalSortFunc: diagnoseResource.sortFuncFfd }
      ];
    },
    /** テーブルに表示する内容のセット */
    setCstmListItem() {
      this.$nextTick(() => {
        this.cstmListItem = this.updateTableItems();
      });
    },
    /** DTC表示画面テーブル情報設定 */
    updateTableItems() {
      if (this.filteredData.length > 0) {
        return this.filteredData.map(e => ({
          dtc: e.code,
          description: resource.getDtcDescriptionResource(e, this),
          tripCount: util.isNullOrDefault(e.igon_time, '-'),
          timeCount: util.isNullOrDefault(e.igon_mill_sec, '-'),
          uniqueCount: convertUniqueCount(e.unique_count, this),
          buttonVisible: e.has_ffd,
          isMultiFFD: e.is_multi_ffd,
          ffd_data: e.data,
          systemID: e.system,
          ffd_status: e.ffd_status,
          ffd_status_name: e.ffd_status === 'ng' ? 'individual.label_status_acquisition_error' : e.ffd_status === 'no' ? 'individual.label_status_no_data' : '-',
          stored_data_number: e.stored_data_number ? e.stored_data_number : '-',
        }));
      } else {
        // データがない時、全て「-」
        return [{
          dtc: this.$t('message.common_hyphen'),
          description: 'message.common_hyphen',
          tripCount: this.$t('message.common_hyphen'),
          timeCount: this.$t('message.common_hyphen'),
          uniqueCount: this.$t('message.common_hyphen'),
          buttonVisible: false,
          ffd_status: this.emptyListFfdStatus,
          ffd_status_name: this.emptyListFfdStatus === 'ng' ? 'individual.label_status_acquisition_error' : this.emptyListFfdStatus === 'no' ? 'individual.label_status_no_data' : '-',
          stored_data_number: '-',
        }];
      }
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },

    /**
     * ファイルエクスポートボタン押下
     * DataRecollection.jsのcsvExportを呼び出している
     * csvExportに一覧のカラム、Itemを引数として渡している.
     */
    async csvExport() {
      this.processing = true;

      // ダミー処理（ファイルエクスポートが高速で終了するので、操作者に通信の発生を認識させる）
      await util.sleep(1000);
      this.$emit('csvExport', this.tableItems, this.cstmListItem, this.recollectedData, false, false, this.immutableId);
      this.processing = false;
    },

    /** FFD押下時の処理 */
    clickFFD(data) {
      if (!data) return;
      // 既にレスポンスとしては取得済みなので、共通処理に流すためにキャッシュ登録を行う
      const cache = this.cachedFFDs.filter(e => e.system === data.systemID && e.dtc === data.dtc && e.measuredId === this.measuresId);
      if (cache.length <= 0) {
        this.cacheFFDStores([...this.cachedFFDs, { system: data.systemID, dtc: data.dtc, data: data.ffd_data, measuredId: this.measuresId }]);
      }
      this.$emit('openFFDModal', data);
    },
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedDetails: state => state.cache.projectDetails,
      cachedFFDs: state => state.cache.ffdStores,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    })
  }
};